import React, { useState, useRef, useCallback, useEffect, memo } from "react";
import PropTypes from "prop-types";
import outside from "../../../utilities/outside";
import { cl } from "../../../utilities/cl";
import { random } from "../../../utilities/random";
import { MEDIA_BREAKPOINT_LG } from "../../../utilities/breakpoints";
import { isTablet, isDesktop } from "../../../utilities/media";
import { changeCollapsible } from "../../../store/Collapsible/actions";
import ButtonCircle from "../../ButtonCircle";
import ButtonWhite from "../../ButtonWhite";
import ElementsGroup from "../../ElementsGroup";
import Button from "../../Button";
import ScrollContainer from "../../ScrollContainer";

import SvgPlus from "../../SVG/Plus";
import SvgArrowDown from "../../SVG/ArrowDown";
import SvgEdit from "../../SVG/Edit";
import SvgTrash from "../../SVG/Trash";
import SvgSave from "../../SVG/Save";
import SvgDownload from "../../SVG/Download";
import SvgSelectAll from "../../SVG/SelectAll";
import SvgClose from "../../SVG/Close";

import { useDispatch, useSelector } from "react-redux";

// Оптимизированные подкомпоненты
const DesktopHeader = memo(
  ({
    title,
    subtitle,
    active,
    contentLoaded,
    elementsLeft,
    elements,
    collapsible,
    onToggle,
    getButton,
  }) => (
    <div className="Collapsible__desktop">
      <div className="left">
        <h2
          className="Collapsible__title"
          onClick={(e) => !collapsible && onToggle(null, e)}
        >
          {title}
          {subtitle && <span className="subtitle">{subtitle}</span>}
        </h2>
        <div className="control">
          {getButton("add")}
          {active && elementsLeft}
          {getButton("toggle-active")}
        </div>
      </div>

      <div className="right">
        <ElementsGroup position="right">
          {getButton("download")}
          {elements}
          {getButton("select")}
          {getButton("cancel")}
          <div className="d-mg-none flex-grow-1" />
          {getButton("edit")}
          {getButton("remove")}
          {getButton("save")}
          {getButton("toggle-active-title")}
        </ElementsGroup>
      </div>
    </div>
  ),
);

const MobileHeader = memo(({ getButton, elements }) => (
  <div className="Collapsible__mobile">
    {getButton("add", true)}
    {elements && <div className="elements">{elements}</div>}
  </div>
));

const Content = memo(({ scroll, scrollData, children }) =>
  scroll.on ? (
    <ScrollContainer size="lg" collapsed={false} {...scrollData}>
      {children}
    </ScrollContainer>
  ) : (
    children
  ),
);

const MobileControl = memo(({ activeMobileControl, getButton }) => {
  const className = cl("CollapsibleMobileControl", {
    active: activeMobileControl,
  });

  return outside(
    <div className={className}>
      {getButton("edit", true)}
      {getButton("download", true)}
      {getButton("remove", true)}
      {getButton("save", true)}
    </div>,
  );
});

// Основной компонент
const Collapsible = ({
  id: propId,
  className = "",
  title = "",
  subtitle,
  notice,
  mobileControl = true,
  scroll = {},
  elements,
  elementsLeft,
  children,
  types = [],
  active: propActive,
  activeDefault = false,
  name,
  collapsible: collapsibleProp,
  buttons,
  onChangeCollapsible,
}) => {
  const dispatch = useDispatch();
  const collapsibleId = useRef(propId || random());
  const refToggle1 = useRef(null);
  const refToggle2 = useRef(null);

  const [active, setActive] = useState(propActive ?? activeDefault);
  const [contentLoaded, setContentLoaded] = useState(
    propActive ?? activeDefault,
  );
  const [activeMobileControl, setActiveMobileControl] = useState(false);

  // Синхронизация с внешними пропсами
  useEffect(() => {
    if (propActive === undefined && activeDefault !== undefined) {
      setActive(activeDefault);
    }
    if (propActive !== undefined) {
      setActive(propActive);
    }
  }, [propActive, activeDefault]);

  // Свернуть другие коллапсиблы в этом пространстве
  const collapseOthers = useCallback((event, currentMode) => {
    if (!isTablet() && !isDesktop()) return;
    if (!currentMode || !event?.clientX) return;

    const thisSpace = refToggle1.current?.closest(".Space__Place");
    if (!thisSpace) return;

    const togglers = thisSpace.querySelectorAll(
      ".Collapsible.active .toggle-active-1",
    );

    for (const toggler of togglers) {
      if (toggler !== refToggle1.current) {
        toggler.click();
      }
    }
  }, []);

  // Переключение активности
  const toggleActive = useCallback(
    (val, event) => {
      setContentLoaded(true);
      setActive((prev) => {
        const newActive = !prev;

        // Отложенный вызов после обновления состояния
        setTimeout(() => collapseOthers(event, newActive), 0);

        // Уведомление родителя
        if (name === "music" || name === "video") {
          if (!prev) dispatch(changeCollapsible(name));
        }
        if (typeof onChangeCollapsible === "function")
          onChangeCollapsible(newActive);

        return newActive;
      });
    },
    [name, dispatch, onChangeCollapsible, collapseOthers],
  );

  // Мемоизированные данные для кнопок
  const scrollData = scroll.data || {};
  const isSimple = types.includes("simple");

  // Получение кнопки (мемоизировано)
  const getButton = useCallback(
    (type, mobile = false) => {
      if (isSimple) return null;
      if (!active && type !== "toggle-active" && type !== "toggle-active-title")
        return null;

      const { add, download, edit, remove, save, select, cancel } =
        buttons || {};

      const buttonMap = {
        select: () =>
          !mobile &&
          select?.on && (
            <Button className="string" onClick={select.onClick}>
              <SvgSelectAll size="xs" title={select.title} />
            </Button>
          ),
        cancel: () =>
          !mobile &&
          cancel?.on && (
            <Button
              className="string"
              disabled={cancel?.disabled}
              onClick={cancel?.onClick}
            >
              <SvgClose size="xs" title={cancel.title} />
            </Button>
          ),
        add: () => {
          if (!add?.on) return null;
          return mobile && add?.mobile !== false ? (
            <ButtonWhite
              title={add.title}
              mode="extend"
              className="plus-mobile"
              disabled={add.disabled}
              onClick={add.onClick}
            >
              <SvgPlus />
            </ButtonWhite>
          ) : (
            <ButtonCircle
              className="plus"
              disabled={add.disabled}
              onClick={add.onClick}
            >
              <SvgPlus />
            </ButtonCircle>
          );
        },
        download: () => {
          if (!download?.on) return null;
          const handler = () => download.onClick?.(download.value);
          return mobile && download?.mobile !== false ? (
            <ButtonCircle
              className="download"
              onClick={handler}
              disabled={download.disabled}
            >
              <SvgDownload />
            </ButtonCircle>
          ) : (
            <Button
              type="string"
              onClick={handler}
              disabled={download.disabled}
            >
              <SvgDownload />
              Скачать
            </Button>
          );
        },
        edit: () => {
          if (!edit?.on) return null;
          const handler = () => edit.onClick?.(edit.value);
          return mobile && edit?.mobile !== false ? (
            <ButtonWhite
              title="Изменить"
              mode="extend"
              onClick={handler}
              disabled={edit.disabled}
            >
              <SvgEdit />
            </ButtonWhite>
          ) : (
            <Button type="string" onClick={handler} disabled={edit.disabled}>
              <SvgEdit className="m-0" />
            </Button>
          );
        },
        remove: () => {
          if (!remove?.on) return null;
          return mobile && remove?.mobile !== false ? (
            <ButtonWhite
              title="Удалить"
              mode="extend"
              disabled={remove.disabled}
              onClick={remove.onClick}
            >
              <SvgTrash />
            </ButtonWhite>
          ) : (
            <Button
              type="string"
              disabled={remove.disabled}
              onClick={remove.onClick}
            >
              <SvgTrash className="m-0" />
            </Button>
          );
        },
        save: () => {
          if (!save?.on) return null;
          return mobile && save?.mobile !== false ? (
            <ButtonWhite
              title="Сохранить"
              mode="extend"
              disabled={save.disabled}
              onClick={save.onClick}
            >
              <SvgSave />
            </ButtonWhite>
          ) : (
            <Button
              type="string"
              disabled={save.disabled}
              onClick={save.onClick}
            >
              <SvgSave className="m-0" />
            </Button>
          );
        },
        "toggle-active": () =>
          !collapsibleProp && (
            <Button
              className="toggle-active toggle-active-1"
              buttonRef={refToggle1}
              type="string"
              onClick={toggleActive}
            >
              <SvgArrowDown
                className="spp-svg-size-mr m-0"
                title={active && contentLoaded ? "Свернуть" : "Развернуть"}
              />
            </Button>
          ),
        "toggle-active-title": () =>
          !collapsibleProp && (
            <Button
              className="toggle-active toggle-active-2"
              buttonRef={refToggle2}
              type="string"
              onClick={toggleActive}
            >
              <SvgArrowDown
                className="spp-svg-size-mr"
                title={active && contentLoaded ? "Свернуть" : "Развернуть"}
              />
              {active ? "Свернуть" : "Развернуть"}
            </Button>
          ),
      };

      return buttonMap[type]?.() || null;
    },
    [active, contentLoaded, buttons, isSimple, collapsibleProp, toggleActive],
  );

  // Мемоизированные классы
  const collapsibleClassName = cl(
    className,
    { Collapsible: types },
    { active },
  );
  const isWide = window.outerWidth >= MEDIA_BREAKPOINT_LG;



  return (
    <section className={collapsibleClassName}>
      <header className="Collapsible__header">
        {isWide ? (
          <DesktopHeader
            title={title}
            subtitle={subtitle}
            active={active}
            contentLoaded={contentLoaded}
            elementsLeft={elementsLeft}
            elements={elements}
            collapsible={collapsibleProp}
            onToggle={toggleActive}
            getButton={getButton}
          />
        ) : (
          <MobileHeader getButton={getButton} elements={elements} />
        )}
        {notice && <div className="Collapsible__notice">{notice}</div>}
      </header>

      {contentLoaded && active && (
        <Content scroll={scroll} scrollData={scrollData}>
          {children}
        </Content>
      )}

      {mobileControl && (
        <MobileControl
          activeMobileControl={activeMobileControl}
          getButton={getButton}
        />
      )}
    </section>
  );
};

Collapsible.propTypes = {
  id: PropTypes.number.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  notice: PropTypes.node,
  mobileControl: PropTypes.bool,
  scroll: PropTypes.shape({
    on: PropTypes.bool,
    data: PropTypes.object,
  }),
  elements: PropTypes.element,
  elementsLeft: PropTypes.element,
  children: PropTypes.node,
  types: PropTypes.arrayOf(PropTypes.oneOf(["simple", "embedded"])),
  active: PropTypes.bool,
  activeDefault: PropTypes.bool,
  name: PropTypes.string,
  collapsible: PropTypes.bool,
  buttons: PropTypes.shape({
    add: PropTypes.object,
    download: PropTypes.object,
    edit: PropTypes.object,
    remove: PropTypes.object,
    save: PropTypes.object,
    select: PropTypes.object,
    cancel: PropTypes.object,
  }),
  onChangeCollapsible: PropTypes.func,
};

export default memo(Collapsible);
